import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Inbox, ArrowLeft, Mail, Phone, CheckCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * VendorInquiries
 * ---------------
 * Simple inbox for messages sent via the Book Now form on the public
 * vendor profile. Newest first. Clicking a row expands it (mark-as-read
 * fires when opening) and reveals the full message + a Reply button
 * (mailto: with prefilled subject so the vendor's default email client
 * pops open — no custom composer to maintain).
 *
 * The dashboard entry card shows an unread badge, so this page always
 * feels alive when there's a new message.
 */
export default function VendorInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor/inquiries?limit=100");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = async (inq) => {
    // Collapsing? Just close.
    if (openId === inq.id) { setOpenId(null); return; }
    setOpenId(inq.id);
    // Fire-and-forget mark-as-read on first open.
    if (!inq.read) {
      try {
        await api.post(`/vendor/inquiries/${inq.id}/read`);
        setItems((prev) => prev.map((x) => x.id === inq.id ? { ...x, read: true } : x));
      } catch { /* soft-fail — badge will resolve on refresh */ }
    }
  };

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    try {
      await api.post("/vendor/inquiries/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      toast.success("All caught up.");
    } catch (err) {
      toast.error("Couldn't update — please try again");
    }
  };

  const unreadCount = items.filter((x) => !x.read).length;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <Link
        to="/vendor/dashboard"
        className="text-sm inline-flex items-center gap-1 mb-6"
        style={{ color: "var(--ink-soft)" }}
        data-testid="inquiries-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>
            Inbox
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-1 leading-tight">
            Inquiries
          </h1>
          <p className="mt-2 text-sm max-w-lg" style={{ color: "var(--ink-soft)" }}>
            Messages customers send through the Book Now button on your public
            profile. Their email is set as reply-to, so replying from your
            inbox lands straight in theirs.
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            type="button"
            data-testid="inquiries-mark-all-read"
            onClick={markAllRead}
            className="btn-outline rounded-full shrink-0"
          >
            <CheckCheck className="h-4 w-4 mr-1.5" /> Mark all read
          </Button>
        )}
      </div>

      {loading && (
        <div className="text-sm" style={{ color: "var(--ink-soft)" }}
             data-testid="inquiries-loading">
          Loading your inbox…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="card-warm rounded-sm p-10 text-center"
             data-testid="inquiries-empty">
          <Inbox className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--ink-soft)" }} />
          <div className="font-serif text-2xl">No messages yet.</div>
          <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: "var(--ink-soft)" }}>
            Prop your booth QR out at your next market — the moment someone
            scans and sends a note, it&apos;ll show up here.
          </p>
        </div>
      )}

      <div className="space-y-2" data-testid="inquiries-list">
        {items.map((inq) => {
          const isOpen = openId === inq.id;
          const mailto = buildMailto(inq);
          return (
            <div
              key={inq.id}
              data-testid={`inquiry-${inq.id}`}
              className="card-warm rounded-sm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleOpen(inq)}
                data-testid={`inquiry-toggle-${inq.id}`}
                className="w-full text-left p-4 flex items-start gap-3 hover:bg-[color:var(--sand)] transition-colors"
              >
                {!inq.read && (
                  <span
                    aria-hidden="true"
                    className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: "var(--terracotta)" }}
                    data-testid={`inquiry-unread-dot-${inq.id}`}
                  />
                )}
                {inq.read && <span className="mt-1.5 h-2 w-2 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div className={`text-sm ${inq.read ? "" : "font-semibold"}`}>
                      {inq.from_name}
                    </div>
                    <div className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
                      {formatWhen(inq.created_at)}
                    </div>
                  </div>
                  <div
                    className="mt-1 text-sm truncate"
                    style={{ color: "var(--ink-soft)" }}
                  >
                    {inq.message}
                  </div>
                </div>
              </button>

              {isOpen && (
                <div
                  className="px-4 pb-4 pt-2 border-t"
                  style={{ borderColor: "var(--sand)" }}
                  data-testid={`inquiry-body-${inq.id}`}
                >
                  <div className="flex flex-wrap gap-3 text-xs mb-3" style={{ color: "var(--ink-soft)" }}>
                    <span className="inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      <a href={`mailto:${inq.from_email}`} className="underline break-all">
                        {inq.from_email}
                      </a>
                    </span>
                    {inq.phone && (
                      <span className="inline-flex items-center gap-1"
                            data-testid={`inquiry-phone-${inq.id}`}>
                        <Phone className="h-3 w-3" />
                        <a href={`tel:${inq.phone}`} className="underline">{inq.phone}</a>
                      </span>
                    )}
                  </div>
                  <p
                    className="text-sm leading-relaxed whitespace-pre-line"
                    data-testid={`inquiry-message-${inq.id}`}
                  >
                    {inq.message}
                  </p>
                  <div className="mt-4">
                    <a
                      href={mailto}
                      data-testid={`inquiry-reply-${inq.id}`}
                      className="btn-primary rounded-full inline-flex items-center gap-1.5 px-5 py-2 text-sm"
                    >
                      <Mail className="h-4 w-4" /> Reply to {firstName(inq.from_name)}
                    </a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function firstName(full) {
  return (full || "").trim().split(/\s+/)[0] || "them";
}

function buildMailto(inq) {
  const subject = encodeURIComponent(`Re: your AddVendTour inquiry`);
  const snippet = (inq.message || "").split("\n").map((l) => "> " + l).join("\n");
  const body = encodeURIComponent(
    `Hi ${firstName(inq.from_name)},\n\n\n\nOn ${formatWhen(inq.created_at)} you wrote:\n${snippet}\n`,
  );
  return `mailto:${inq.from_email}?subject=${subject}&body=${body}`;
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const secs = (now - d) / 1000;
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch {
    return iso;
  }
}
